import { useWorldStore } from '../state/worldStore';
import { routeBetween, along, snapWalkable, pathsReady } from './cityPath';
import { baseGroundY, groundY } from './cityGround';
import { driveRoute, drivePos } from './cityDrive';
import { roadMapBridge, coveredAt } from '../render/roadMapBridge';
import { yawToward } from './facing';
import { stagePose } from './stagePlan';

// cityMotion — where each artist is, from their timeline and the clock.
//
// PURE READING of the server's decisions (API services/city/cityTimeline): a
// segment says they are staying at a place (on a spot), walking from one place
// to another between two instants, or away. This turns that into a position and
// a heading for an instant. Every viewer runs the same function on the same
// timeline and the same clock, so they all see the artist in the same place.
//
// Places come in two kinds. A VENUE FRONT is stored relative to its venue's
// anchor, because venues can be moved by hand in the World; it is resolved
// against the live anchors here. An OUTDOOR place is absolute.

// How high the plaza stage's floor is above the lot it stands on
// (components/city/CityPlaces builds it): someone performing stands ON it.
export const STAGE_Y = 0.32;

let anchorsRef = null;
let snapRef = null;
const spotCache = new Map();   // slug → [[x, z], …] (every spot of the place)
// A new road map (a shell stamped in, a rebuilt city) moves where spots snap.
roadMapBridge.subscribe(() => spotCache.clear());

// A VENUE TURNED BY HAND (the venue brush's rotation, `venue_anchor` rotY)
// turns its place with it: offsets, the line the spots stand along, and which
// way they face are all in the venue's own frame. The Corner Market was turned
// 270° and its spots stayed pointing the old way — into the shop (2026-10-24).
//
// A REGISTRY venue (city/venueRegistry) has no row: its turn is its SITE's — the
// building's front faces the street at site.rotY, and its place (the pavement in
// front of the door, `offset` in the building's own frame) has to turn with it
// or it lands behind the building (2026-10-30). A saved row still wins.
function venueTurn(place) {
  if (!place?.anchor) return 0;
  const s = useWorldStore.getState();
  const o = s.venueAnchorOverrides?.[place.anchor];
  if (o) return Number(o.rotY) || 0;
  return Number(s.venueRegistry?.[place.anchor]?.site?.rotY) || 0;
}
/**
 * Which way is OUT, to the street, from a venue's front (rotationY).
 *
 * ⚠ NOT `place.facing` + π (2026-10-31). `facing` is how someone on their own
 * stands there, and the places disagree on what that is: at most fronts it is
 * the window (browsing, π for a +z storefront), but at the Studio, the Concert
 * Hall and the Underground it is the street (waiting, 0). Turning `facing` half
 * round to get "the street" stood every narrator and every odd spot at those
 * three with their face to the wall.
 *
 * The geometry says it without anyone's intent: the place is the pavement in
 * front of the door, at `offset` in the building's own frame, so the street is
 * the way the offset points — to the nearest quarter turn (a pavement runs
 * square to its building), turned with the building.
 */
function streetYaw(place) {
  // A place stood at a door placed by hand (no measured building, so no
  // offset: The Warehouse) says where its door is: out is away from it.
  if (Array.isArray(place?.door) && Array.isArray(place?.at)) return yawToward(place.door, place.at);
  const o = place?.offset;
  if (!o || (!o[0] && !o[1])) return (place?.facing ?? 0) + venueTurn(place);
  const q = Math.PI / 2;
  return Math.round(Math.atan2(o[0], o[1]) / q) * q + venueTurn(place);
}
/** [x, z] turned by θ about the vertical (three.js rotation.y). */
function turn(v, th) {
  if (!th) return v;
  const c = Math.cos(th); const s = Math.sin(th);
  return [v[0] * c + v[1] * s, -v[0] * s + v[1] * c];
}

/** Does the motion know the city's places yet (a snapshot has been given)? */
export const geoReady = () => !!snapRef?.places?.length;

/**
 * Called whenever the snapshot or the venue anchors change. A snapshot with no
 * places (none has arrived yet) never wipes places already known — a replay
 * gives them before the live city does (city/replay), and losing them put
 * everyone at the origin for the frames until the next snapshot.
 */
export function setGeo(snap, anchors) {
  if (!snap?.places?.length && snapRef?.places?.length) snap = snapRef;
  if (snap !== snapRef || anchors !== anchorsRef) spotCache.clear();
  snapRef = snap;
  anchorsRef = anchors;
}

function placeOf(slug) {
  return snapRef?.places?.find((p) => p.slug === slug) || null;
}

/** A place's centre, [x, z], against the live venue anchors. */
export function placeCentre(place) {
  if (!place) return [0, 0];
  const a = place.anchor && anchorsRef?.[place.anchor];
  if (a && Array.isArray(place.offset)) { const o = turn(place.offset, venueTurn(place)); return [a[0] + o[0], a[2] + o[1]]; }
  return place.at || [0, 0];
}

/**
 * Where someone stands on a spot at a place, snapped onto clear pavement.
 *
 * A place's spots are resolved TOGETHER: on a narrow pavement two wanted spots
 * can snap to the same patch of ground, and two people would stand inside each
 * other. Each spot after the first keeps a step clear of the ones before it,
 * sliding along the pavement until it is.
 */
const MIN_APART = 0.42;
function resolvePlace(slug) {
  const place = placeOf(slug);
  if (!place) return null;
  const c = placeCentre(place);
  const th = venueTurn(place);
  const n = (place.line ? snapRef?.line?.length : snapRef?.spots?.length) || 10;
  const out = [];
  for (let i = 0; i < n; i++) {
    let want;
    if (place.line && Array.isArray(snapRef?.line)) {
      const d = snapRef.line[i] ?? 0;
      const o = turn(place.line === 'z' ? [0, d] : [d, 0], th);
      want = [c[0] + o[0], c[1] + o[1]];
    } else {
      const off = turn(snapRef?.spots?.[i] || [0, 0], th);
      want = [c[0] + off[0], c[1] + off[1]];
    }
    const radius = place.line ? 1.2 : 3.5;
    let p = snapWalkable(want[0], want[1], { radius });
    const clear = (q) => out.every((o) => Math.hypot(o[0] - q[0], o[1] - q[1]) >= MIN_APART);
    if (!clear(p)) {
      // Slide along the pavement (or outward, on open ground) until it is clear.
      const dir = turn(place.line === 'z' ? [0, 1] : [1, 0], th);
      for (let k = 1; k <= 12 && !clear(p); k++) {
        const sgn = k % 2 ? 1 : -1; const step = Math.ceil(k / 2) * 0.3 * sgn;
        const q = snapWalkable(want[0] + dir[0] * step, want[1] + dir[1] * step, { radius: 0.6 });
        if (clear(q)) p = q;
      }
    }
    out.push(p);
  }
  return out;
}

/**
 * A place's PROPS where they stand (API cityPlaces `props`: the crates outside
 * Wax & Vinyl, the produce stand, a poster, road cases): [{ kind, use, at: [x, z],
 * rotY }]. Laid along the place's pavement in the VENUE'S OWN FRAME, so a venue
 * turned by hand turns its props with it (they used to stay put while its
 * spots turned). Drawn by CityPlaces' VenueTouches from this same answer.
 */
export function placeProps(slugOrPlace) {
  const place = typeof slugOrPlace === 'string' ? placeOf(slugOrPlace) : slugOrPlace;
  if (!place?.props?.length) return [];
  const c = placeCentre(place);
  const th = venueTurn(place);
  return place.props.map((pr) => {
    const o = turn(place.line === 'z' ? [0, pr.d] : [pr.d, 0], th);
    return { kind: pr.kind, use: Number(pr.use) || 0, at: [c[0] + o[0], c[1] + o[1]], rotY: (place.line === 'z' ? Math.PI / 2 : 0) + th };
  });
}

// How a prop is used: at its two ENDS along the pavement (crates, a produce
// stand, road cases: you browse from the side), or in FRONT of it (a poster
// you stop to read). Standing spots `u<prop>.<n>` (API assignSpots).
const USE_FROM = { poster: 'front' };
const USE_GAP = 0.75;
function useSpot(place, spot) {
  const m = /^u(\d+)\.(\d+)$/.exec(String(spot));
  const props = m ? placeProps(place) : [];
  const pr = m ? props[Number(m[1])] : null;
  if (!pr) return null;
  const k = Number(m[2]);
  let want;
  if (USE_FROM[pr.kind] === 'front') {
    const side = (k % 2 ? 1 : -1) * Math.floor((k + 1) / 2) * 0.45;   // a second reader beside the first
    want = [pr.at[0] + Math.sin(pr.rotY) * 0.7 + Math.cos(pr.rotY) * side, pr.at[1] + Math.cos(pr.rotY) * 0.7 - Math.sin(pr.rotY) * side];
  } else {
    // The prop runs along its local x (its rotY lays that along the pavement).
    const sgn = k % 2 ? 1 : -1; const d = USE_GAP * (1 + Math.floor(k / 2) * 0.6);
    want = [pr.at[0] + Math.cos(pr.rotY) * d * sgn, pr.at[1] - Math.sin(pr.rotY) * d * sgn];
  }
  const at = snapWalkable(want[0], want[1], { radius: 0.5 });
  return { at, facing: yawToward(at, pr.at) };
}

/** A guest spot (`g0`…): free time INSIDE the venue's room (API city/guestRooms; World RoomGuests). */
export const isGuestSpot = (spot) => typeof spot === 'string' && /^g\d+$/.test(spot);

export function spotPos(slug, spot) {
  const place = placeOf(slug);
  if (!place) return [0, 0];
  if (spot === 'stage') return place.stage?.at || placeCentre(place);
  // Going IN (2026-11-01): the walk ends at the front of the door and the street
  // stops drawing them there (`inside`) — not on pavement spot 0 beside it.
  if (isGuestSpot(spot)) return placeCentre(place);
  if (typeof spot === 'string' && spot[0] === 'u') {
    const ukey = `${slug}:${spot}`;
    let u = spotCache.get(ukey);
    if (!u || !pathsReady()) { u = useSpot(place, spot); if (u && pathsReady()) spotCache.set(ukey, u); }
    if (u) return u.at;
  }
  const key = slug;
  let list = spotCache.get(key);
  if (!list || !pathsReady()) {
    list = resolvePlace(slug);
    if (list && pathsReady()) spotCache.set(key, list);
  }
  return list?.[Number(spot) || 0] || placeCentre(place);
}

const isPairSpot = (spot) => (snapRef?.pairs || [[0, 1], [2, 3]]).some((p) => p.includes(spot));
/** Is there a building right in front of someone facing `heading` here? (the road map's covered texels) */
function facesWall(x, z, heading) {
  return coveredAt(x + Math.sin(heading) * 1.0, z + Math.cos(heading) * 1.0, false);
}

/** Which way someone on a spot faces (rotationY; 0 = +z, out toward the street). */
export function spotFacing(slug, spot) {
  const place = placeOf(slug);
  if (spot === 'stage') return place?.stage?.facing ?? 0;
  // At a prop: facing it (the crates, the poster), whatever the place faces.
  if (typeof spot === 'string' && spot[0] === 'u') {
    spotPos(slug, spot);
    const u = spotCache.get(`${slug}:${spot}`) || (place ? useSpot(place, spot) : null);
    if (u) return u.facing;
  }
  const pairs = snapRef?.pairs || [[0, 1], [2, 3]];
  // A pair faces each other: the first of them looks along +axis, the second back.
  const th = venueTurn(place);
  const along = place?.line === 'z' ? [0, Math.PI] : [Math.PI / 2, -Math.PI / 2];
  for (const [a, b] of pairs) {
    if (Number(spot) === a) return along[0] + th;
    if (Number(spot) === b) return along[1] + th;
  }
  // NOT A ROW OF PEOPLE STARING AT A WALL (2026-10-30): on their own, every
  // other spot looks out at the street instead of at the window.
  const n = Number(spot);
  if (place?.kind === 'venue' && Number.isFinite(n) && n % 2 === 1) return streetYaw(place);
  return (place?.facing ?? 0) + th;
}

function entryPos(slug) {
  const e = snapRef?.entries?.find((x) => x.slug === slug);
  if (!e) return [0, 0];
  if (e.place) return spotPos(e.place, 4);
  return snapWalkable(e.at[0], e.at[1], { radius: 4 });
}

function endPos(end, nextSpot) {
  if (!end) return [0, 0];
  if (end.entry) return entryPos(end.entry);
  return spotPos(end.place, nextSpot ?? 4);
}

// ── Routes, worked out off the frame ─────────────────────────────────────────
// A* is a few milliseconds to a few tens; doing it inside a frame is a hitch.
// So a walk asks for its route and gets the straight line until the real one has
// been worked out in idle time.
const routes = new Map();     // key → route
const pending = new Set();
const queue = [];
const idle = typeof window !== 'undefined' && window.requestIdleCallback
  ? (fn) => window.requestIdleCallback(fn, { timeout: 400 })
  : (fn) => setTimeout(fn, 30);
function pump() {
  idle(() => {
    const job = queue.shift();
    if (job) {
      try { routes.set(job.key, routeBetween(job.a, job.b)); } catch { /* straight line stays */ }
      pending.delete(job.key);
    }
    if (queue.length) pump();
  });
}
function routeFor(a, b) {
  const key = `${a[0].toFixed(2)},${a[1].toFixed(2)}>${b[0].toFixed(2)},${b[1].toFixed(2)}`;
  const r = routes.get(key);
  if (r && (r.real || !pathsReady())) return r;
  if (!pending.has(key) && pathsReady()) {
    pending.add(key);
    queue.push({ key, a, b });
    if (queue.length === 1) pump();
  }
  const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
  return { pts: [a, b], cum: [0, len], len, real: false };
}
if (typeof window !== 'undefined') window.addEventListener?.('beforeunload', () => { queue.length = 0; });

// ── The segment at an instant ────────────────────────────────────────────────
function segIndex(segs, t) {
  let lo = 0; let hi = segs.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const s = segs[mid];
    if (t < s.t0) hi = mid - 1;
    else if (t >= s.t1) lo = mid + 1;
    else return mid;
  }
  return -1;
}

/**
 * Where a resident is at `t`, or null when they are not in the city.
 * { x, z, heading, moving, speed, mode, activity, place, convId, show, seg }
 *
 * `convs` (id → conversation) puts two artists who ran into each other on a
 * pair of spots facing each other for as long as they talk.
 */
export function stateAt(resident, t, convs) {
  const segs = resident?.segs || [];
  const i = segIndex(segs, t);
  if (i < 0) return null;
  const s = segs[i];
  if (s.k === 'away') return null;

  if (s.k === 'stay') {
    // A chance encounter is not on anyone's timeline — it is laid over it.
    let slug = s.p; let spot = s.s ?? 4; let convId = s.c || null; let mode = s.m;
    if (!convId && convs) {
      for (const c of Object.values(convs)) {
        if (c.origin !== 'encounter' || c.status !== 'live' || c.place !== s.p) continue;
        if (!(c.a === resident.avatarId || c.b === resident.avatarId)) continue;
        if (t < c.t0 || t >= c.t1) continue;
        const pair = (snapRef?.pairs || [[0, 1], [2, 3]])[c.pair || 0];
        spot = c.a === resident.avatarId ? pair[0] : pair[1];
        convId = c.id; mode = 'story'; slug = c.place;
        break;
      }
    }
    // A STILL'S OWN MARK (city/moment, 2026-11-01): a staged moment stands the
    // pair closer than their spots and turned a little toward the lens — `xz`
    // and `hd` on the stay. Live timelines never carry them.
    const [x, z] = Array.isArray(s.xz) ? s.xz : spotPos(slug, spot);
    let heading = s.hd != null ? s.hd : spotFacing(slug, spot);
    // ALONE IN A STORY SCENE, THEY TELL IT TO THE STREET. At a venue front a
    // spot faces the building (browsing the window); someone narrating their
    // day turns to the open street instead, or every shot of them is their back.
    // ⚠ AT A VENUE, ALWAYS (2026-10-29: an episode at the Corner Market played
    // with her facing the shop, narrating as if inside). The wall probe looks
    // one unit ahead, so a spot set back further, or a road map not loaded yet,
    // read as "no wall" and she kept facing the window. At a venue the street is
    // known from the place itself (streetYaw); the probe stays for anywhere else.
    if (s.st && (s.sc ?? -1) >= 0 && !convId && !isPairSpot(spot)) {
      const pl = placeOf(slug);
      // Told to the street: SET to face it at a venue (a spot that already looked
      // out would have been flipped back to the wall), turned only off a wall elsewhere.
      if (pl?.kind === 'venue') heading = streetYaw(pl);
      else if (facesWall(x, z, heading)) heading += Math.PI;
    }
    // `inside`: in that venue's room for a story scene (API assignSpots `r`); the
    // street shows nobody there (CityResident), the room does (RoomStory).
    // PLAYING A SET (city/stagePlan, 2026-11-04): where on the stage they are and
    // what they are doing at this moment of it, the same for everyone. `move` is a
    // library move for their body (CityResident); `stageWalk` marks a walk between
    // marks, so scene view keeps them in frame while they cross.
    const sp = s.sh && spot === 'stage' ? stagePose(snapRef?.shows, resident.avatarId, placeOf(slug)?.stage, t) : null;
    if (sp) {
      return { x: sp.x, z: sp.z, y: baseGroundY(x, z) + STAGE_Y, heading: sp.heading, moving: sp.moving, speed: sp.speed, move: sp.move, stageWalk: sp.moving, gait: sp.gait, mode, activity: s.a || null, place: slug, spot, convId, show: true, story: s.st || null, scene: s.sc ?? null, inside: s.r || null, seg: s };
    }
    return { x, z, y: spot === 'stage' ? baseGroundY(x, z) + STAGE_Y : groundY(x, z), heading, moving: false, speed: 0, mode, activity: s.a || null, place: slug, spot, convId, show: !!s.sh, story: s.st || null, scene: s.sc ?? null, inside: s.r || null, seg: s };
  }

  // Walking. From where the last stay left them to where the next one starts.
  const next = segs[i + 1];
  const prev = segs[i - 1];
  // A trip in parts (below) names its own spots, so every part agrees on its ends.
  const fromSpot = s.fs != null ? s.fs : prev && prev.k === 'stay' && s.f?.place === prev.p ? prev.s : undefined;
  const toSpot = s.ts != null ? s.ts : next && next.k === 'stay' && s.to?.place === next.p ? next.s : undefined;
  const a = endPos(s.f, fromSpot);
  const b = endPos(s.to, toSpot);
  // A TRIP SHOWN IN PARTS (a replayed episode, city/replay): `pr` is the share of
  // the whole trip this segment covers ([from, to], 0..1) and `full` how long the
  // whole trip takes; the part before it and the part after are other segments,
  // with a cut between them (`cut` on the later one). Live timelines have neither.
  const dur = Math.max(1, s.t1 - s.t0);
  const local = Math.max(0, Math.min(1, (t - s.t0) / dur));
  const pr = Array.isArray(s.pr) ? s.pr : null;
  const p = pr ? pr[0] + (pr[1] - pr[0]) * local : local;
  const endOfTrip = pr ? pr[1] >= 1 : true;
  // A City Story's car ride (API cityTimeline 'drive'): on foot to the line,
  // by car along the city's route lines, on foot to the next scene (cityDrive).
  if (s.k === 'drive') {
    const d = drivePos(driveRoute(a, b), p, Math.max(1, s.full || dur));
    return { x: d.x, z: d.z, y: d.driving ? baseGroundY(d.x, d.z) : groundY(d.x, d.z), heading: d.heading, moving: d.moving && (local < 1 || !endOfTrip), speed: d.speed, driving: d.driving, mode: s.m, activity: null, place: s.to?.place || null, convId: null, show: false, story: s.st || null, scene: null, seg: s };
  }
  const route = routeFor(a, b);
  const at = along(route, p * route.len);
  const speed = (route.len * (pr ? pr[1] - pr[0] : 1)) / (dur / 1000);
  return { x: at.x, z: at.z, y: groundY(at.x, at.z), heading: at.heading, moving: p > 0 && (p < 1 || !endOfTrip) && route.len > 0.05, speed, mode: s.m, activity: null, place: s.to?.place || null, convId: null, show: false, story: s.st || null, scene: null, seg: s };
}

/** When this resident's free time ends (their next story or leaving), or null. */
export function freeUntil(resident, t) {
  for (const s of resident?.segs || []) {
    if (s.t1 <= t) continue;
    if (s.t0 > t && (s.k === 'away' || s.m === 'story')) return s.t0;
  }
  return null;
}

// DEV ONLY — probes read positions through CDP (main world). Not in production.
if (import.meta.env.DEV && typeof window !== 'undefined') window.__cityMotion = { stateAt, spotPos, placeCentre };
